import React, { useState, useEffect } from 'react';
import { confirmSignUp, signIn, fetchAuthSession } from 'aws-amplify/auth'; // Add fetchAuthSession
import { useNavigate } from 'react-router-dom';
import { apiPost } from '../utils/api';

const ConfirmEmailView = ({ email: propEmail, setIsAuthenticated, setHasBilling }) => {
  const [email, setEmail] = useState(propEmail || sessionStorage.getItem("pendingEmail") || '');
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    console.log("ConfirmEmailView mounted with email:", email);
  }, [email]);

  const handleConfirm = async () => {
    if (!email.trim()) {
      setMessage("Please enter your email.");
      return;
    }
    if (!code.trim()) {
      setMessage("Please enter the confirmation code.");
      return;
    }

    try {
      setIsLoading(true);
      setMessage("");

      // Step 1: Confirm the sign-up
      await confirmSignUp({ username: email, confirmationCode: code });
      console.log("✅ User confirmed successfully!");

      const storedEmail = sessionStorage.getItem("pendingEmail") || email;
      const storedPassword = sessionStorage.getItem("pendingPassword");

      if (!storedEmail || !storedPassword) {
        setMessage("Email confirmed! Redirecting to login...");
        // Small delay to show the message, then navigate
        setTimeout(() => navigate('/login'), 1500);
        return;
      }

      // Step 2: Perform auto-login
      const signInResponse = await signIn({
        username: storedEmail,
        password: storedPassword,
      });
      
      console.log("✅ Sign-in response:", signInResponse);

      // Step 3: Fetch the session to get tokens (required in Amplify v6)
      const session = await fetchAuthSession();
      console.log("✅ Fetched session");
      
      // Step 4: Extract userId from the session tokens
      const userId = session.tokens?.idToken?.payload?.sub;
      
      if (!userId) {
        console.error("❌ Could not extract userId from session");
        sessionStorage.removeItem("pendingEmail");
        sessionStorage.removeItem("pendingPassword");
        setMessage("Account verified! Redirecting to setup billing...");
        setHasBilling(false);
        setIsAuthenticated(true);
        // App.js useEffect will handle the redirect automatically
        return;
      }

      console.log("✅ User ID:", userId);
      
      // Store auth data in session storage
      sessionStorage.setItem('authToken', session.tokens.idToken.toString());
      sessionStorage.setItem('userId', userId);
      
      // Step 5: Check billing status
      try {
        console.log("🔍 Checking billing status...");
        const billingResponse = await apiPost('/get-payment-methods', { userId });
        const hasPaymentMethods = billingResponse.paymentMethods && 
                                billingResponse.paymentMethods.length > 0;
        
        console.log("💳 Has payment methods:", hasPaymentMethods);

        // Clean up stored credentials
        sessionStorage.removeItem("pendingEmail");
        sessionStorage.removeItem("pendingPassword");

        // Set message and authentication state
        if (!hasPaymentMethods) {
          setMessage("Account verified! Redirecting to setup billing...");
        } else {
          setMessage("Account verified! Redirecting to dashboard...");
        }

        // Set state - App.js useEffect will handle the redirect automatically
        setHasBilling(hasPaymentMethods);
        setIsAuthenticated(true);
      } catch (billingError) {
        console.error("❌ Error checking billing:", billingError);
        // If billing check fails, redirect to billing setup to be safe
        sessionStorage.removeItem("pendingEmail");
        sessionStorage.removeItem("pendingPassword");
        setMessage("Account verified! Redirecting to setup billing...");
        setHasBilling(false);
        setIsAuthenticated(true);
        // App.js useEffect will handle the redirect automatically
      }
    } catch (error) {
      console.error("❌ Confirmation error:", error);
      setMessage(error.message || "An error occurred during confirmation.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '400px', margin: '0 auto', padding: '20px' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Confirm Your Email</h2>
      
      <p style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
        We've sent a confirmation code to your email address. Please enter it below to complete your registration.
      </p>
      
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Email</label>
        <input 
          type="email" 
          value={email} 
          onChange={(e) => setEmail(e.target.value)} 
          style={{ 
            width: '100%', 
            padding: '10px', 
            border: '1px solid #ddd', 
            borderRadius: '4px',
            fontSize: '16px'
          }}
          placeholder="Enter your email"
        />
      </div>
      
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Confirmation Code</label>
        <input 
          type="text" 
          value={code} 
          onChange={(e) => setCode(e.target.value)} 
          style={{ 
            width: '100%', 
            padding: '10px', 
            border: '1px solid #ddd', 
            borderRadius: '4px',
            fontSize: '16px'
          }}
          placeholder="Enter the 6-digit code"
        />
      </div>
      
      <button 
        onClick={handleConfirm}
        disabled={isLoading}
        style={{ 
          width: '100%', 
          padding: '12px', 
          backgroundColor: isLoading ? '#ccc' : '#007bff', 
          color: 'white', 
          border: 'none', 
          borderRadius: '4px',
          fontSize: '16px',
          cursor: isLoading ? 'not-allowed' : 'pointer'
        }}
      >
        {isLoading ? "Confirming..." : "Confirm Email"}
      </button>
      
      {message && (
        <div style={{ 
          marginTop: '15px', 
          padding: '10px', 
          backgroundColor: message.includes('error') || message.includes('failed') ? '#f8d7da' : '#d4edda',
          color: message.includes('error') || message.includes('failed') ? '#721c24' : '#155724',
          border: `1px solid ${message.includes('error') || message.includes('failed') ? '#f5c6cb' : '#c3e6cb'}`,
          borderRadius: '4px'
        }}>
          {message}
        </div>
      )}
      
      {/* ADDED: Privacy Policy and Terms Links */}
      <div style={{ 
        marginTop: '20px', 
        paddingTop: '16px', 
        borderTop: '1px solid #e9ecef',
        textAlign: 'center'
      }}>
        <p style={{ 
          fontSize: '12px', 
          color: '#6c757d', 
          margin: '0',
          lineHeight: '1.4'
        }}>
          By completing registration, you agree to our{' '}
          <a 
            href="https://smack.fm/privacy" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#007bff', textDecoration: 'underline' }}
          >
            Privacy Policy
          </a>
          {' '}and{' '}
          <a 
            href="https://smack.fm/terms" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#007bff', textDecoration: 'underline' }}
          >
            Terms of Service
          </a>
        </p>
      </div>
    </div>
  );
};

export default ConfirmEmailView;